'use client';

import {
  TabsContentSlot,
  TabsLeadingIconSlot,
  TabsListSlot,
  TabsSpinnerSlot,
  TabsTooltipContentSlot,
  TabsTooltipSlot,
  TabsTrailingIconSlot,
  TabsTriggerSlot,
} from '@magicschool/spellbook/components/Tabs/slots';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as TabsPrimitive from 'radix-ui/tabs';
import { useRef } from 'react';

export type TabsProps = React.ComponentProps<typeof TabsPrimitive.Root> &
  VariantProps<typeof tabsVariants> & {
    /** If true, the tab trigger will display a loading spinner icon and click functionality will be disabled. */
    isLoading?: boolean;
    /** If true, all tabs with be disabled by default */
    disabled?: boolean;
    /** Default focus behavior for the tabs is 'off', meaning that the expectation is there is focusable material expected within each tab content panel. If you want the tabs to focus on the content panel itself and there are no focusable elements within the panel, set this to true. */
    enableDefaultFocus?: boolean;
  };

export const tabsVariants = cva('flex flex-col data-[orientation=vertical]:flex-row', {
  variants: {
    /** If true, the width of the tabs container will be 100% */
    fullWidth: {
      true: 'w-full',
      false: 'w-fit',
    },
    /** The alignment of the tabs relative to the container. Only relevant if `fullWidth` is true. */
    justifyTabs: {
      stretch: '',
      center: '',
      start: '',
      end: '',
    },
    /** The visual appearance of the tabs. */
    variant: {
      default: '',
      underline: '',
      chip: '',
    },
    /** The size of the tabs. */
    size: {
      sm: 'gap-1',
      md: 'gap-2',
      lg: 'gap-2',
    },
  },
});

export const TabsBase: React.FC<TabsProps> = ({
  children,
  className,
  disabled,
  defaultValue,
  enableDefaultFocus = false,
  fullWidth = false,
  isLoading = false,
  justifyTabs = 'stretch',
  onValueChange,
  size = 'md',
  value: providedValue,
  variant = 'default',
  ...props
}) => {
  // Radix Tabs 1.2 focuses the trigger on mousedown, and that focus selects it a second time in the same click.
  const reportedThisTurn = useRef<string | null>(null);
  const handleValueChange = (next: string) => {
    if (next === reportedThisTurn.current) return;
    reportedThisTurn.current = next;
    queueMicrotask(() => {
      reportedThisTurn.current = null;
    });
    onValueChange?.(next);
  };

  const { slots } = useSlots(children, {
    list: { Component: TabsListSlot, props: { fullWidth, variant, size, justifyTabs, isLoading, disabled } },
    contents: { Component: TabsContentSlot, props: { enableDefaultFocus } },
  });

  return (
    <TabsPrimitive.Root
      data-spellbook-name="tabs"
      className={cn(tabsVariants({ fullWidth, variant, justifyTabs, size }), className)}
      {...props}
      activationMode="automatic"
      asChild={false}
      value={providedValue}
      defaultValue={defaultValue}
      onValueChange={handleValueChange}
    >
      {slots.list}
      {slots.contents}
    </TabsPrimitive.Root>
  );
};

/**
 * Compound tab component built on Radix Tabs for organizing content into switchable sections.
 *
 * ### When to use
 * - For grouping related content sections that share the same page context (e.g., settings panels, dashboard views)
 * - When users need to switch between views without navigating to a new page
 *
 * ### When NOT to use
 * - For step-by-step workflows: use a stepper or wizard pattern instead
 * - For primary page navigation: use `NavLink` or route-based navigation instead
 * - For toggling a single option: use `Toggle` or `Switch` instead
 *
 * ### API
 * - `value` + `onValueChange`: Controlled mode — manage the active tab externally (e.g., synced with URL or store)
 * - `defaultValue`: Uncontrolled mode — sets the initially active tab and lets the component manage state
 * - `variant`: `'default'` for pill-style tabs, `'underline'` for underline-style tabs, `'chip'` for neutral pill-shaped chip tabs
 * - `size`: `'sm'` for compact tabs, `'md'` (default) for standard tabs, `'lg'` for larger tabs
 * - `fullWidth`: Expands the tabs container to fill its parent
 * - `justifyTabs`: Aligns triggers within the list: `'stretch'` (default), `'center'`, `'start'`, or `'end'`
 * - `orientation`: `'horizontal'` (default) or `'vertical'` for side-by-side layout
 * - `isLoading`: Shows a spinner on all triggers and disables interaction
 * - `disabled`: Disables all tab triggers
 * - `enableDefaultFocus`: When `true`, the content panel receives focus on tab switch. Keep `false` (default) when the panel contains interactive elements
 *
 * ### Composition (slots)
 * - `<Tabs.List>`: Container for tab triggers. Must be a direct child of `Tabs`
 * - `<Tabs.Trigger>`: Individual tab button. Requires a `value` matching a `<Tabs.Content>`. Supports `withBorder` for bordered style. Do not combine `withBorder` with `variant="chip"` — the chip variant has borders built in and `withBorder`'s active-state stroke token will conflict.
 * - `<Tabs.Content>`: Panel rendered when its matching trigger is active. Requires a `value`
 * - `<Tabs.LeadingIcon>`: Icon before the trigger label. Use within `<Tabs.Trigger>`. Use `overrideDefault` to render custom content like an `Avatar`
 * - `<Tabs.TrailingIcon>`: Icon after the trigger label. Use within `<Tabs.Trigger>`
 * - `<Tabs.Spinner>`: Loading spinner within a trigger. Use within `<Tabs.Trigger>`
 * - `<Tabs.Tooltip>`: Tooltip wrapper around a trigger. Use within `<Tabs.List>`, wrapping `<Tabs.Trigger>` and `<Tabs.TooltipContent>`
 * - `<Tabs.TooltipContent>`: Content for the trigger tooltip. Use within `<Tabs.Tooltip>`
 *
 * ### Accessibility
 * - Arrow key navigation between triggers is handled automatically by Radix
 * - Uses `activationMode="automatic"` — tabs activate on focus, not on click
 * - Provide `aria-label` on the root `Tabs` when the purpose isn't clear from surrounding context
 */
export const Tabs = Object.assign(TabsBase, {
  List: TabsListSlot,
  Trigger: TabsTriggerSlot,
  Tooltip: TabsTooltipSlot,
  TooltipContent: TabsTooltipContentSlot,
  Content: TabsContentSlot,
  LeadingIcon: TabsLeadingIconSlot,
  TrailingIcon: TabsTrailingIconSlot,
  Spinner: TabsSpinnerSlot,
});
